import * as React from 'react'
import { ThemeProvider } from '@emotion/react'
import { Alert, Box, Button, Chip, Container, IconButton, Paper, Snackbar, Tooltip, Typography } from '@mui/material'
import MUIDataTable from 'mui-datatables'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import LinkIcon from '@mui/icons-material/Link'
import theme from '../theme'
import api from '../services/api'
import { useAuth } from '../auth/AuthProvider'
import { useNavigate } from 'react-router-dom'
import FormDialog from './FormDialog'
import GuiaWebhooks from './GuiaWebhooks'

export default function Webhook() {
    const navigate = useNavigate()
    const auth = useAuth()
    const [handlerDialog, setHandlerDialog] = React.useState(false)
    const [handlerGuia, setHandlerGuia] = React.useState(false)
    const [update, setUpdate] = React.useState(false)
    const [data, setData] = React.useState([])
    const [snack, setSnack] = React.useState({ open: false, message: '' })

    React.useEffect(() => {
        setUpdate(false)
        updateData()
    }, [handlerDialog, update])

    React.useEffect(() => {
        document.title = "Horus - Webhooks"
    }, [])

    const updateData = () => {
        api.get('webhook-tokens/')
            .then(res => setData(res.data))
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    navigate('/401')
                }
                setData([])
            })
    }

    const deleteItem = (id, empresa) => {
        const conf = confirm(`Deseja realmente desativar o token da empresa ${empresa}?`)
        if (!conf) return
        api.delete(`webhook-tokens/${id}`).then(() => {
            setUpdate(true)
        }).catch(() => alert('Algum erro aconteceu...'))
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setSnack({ open: true, message: 'Copiado!' })
        })
    }

    const getWebhookUrl = (source, token) => {
        const baseUrl = process.env.REACT_APP_API_URL || window.location.origin
        return `${baseUrl}/api/v1/webhooks/${source}?token=${token}`
    }

    const columns = [
        {
            name: 'id',
            label: 'Id',
            options: { display: false }
        },
        {
            name: 'cod_empresa',
            label: 'Empresa',
            options: { filter: true, sort: true }
        },
        {
            name: 'source',
            label: 'Source',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value) => (
                    <Chip
                        label={value}
                        size="small"
                        color={value === 'zabbix' ? 'primary' : 'secondary'}
                        variant="outlined"
                    />
                )
            }
        },
        {
            name: 'token',
            label: 'Token',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <code style={{ fontSize: '0.75rem' }}>
                            {value ? value.substring(0, 16) + '...' : ''}
                        </code>
                        <Tooltip title="Copiar token">
                            <IconButton size="small" onClick={() => copyToClipboard(value)}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        },
        {
            name: 'created_at',
            label: 'Criado em',
            options: { filter: false, sort: true }
        },
        {
            name: 'actions',
            label: 'Acoes',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const row = data[dataIndex]
                    if (!row) return null
                    return (
                        <>
                            <Tooltip title="Copiar URL completa do webhook">
                                <Button
                                    sx={{ color: theme.palette.text.primary }}
                                    onClick={() => copyToClipboard(getWebhookUrl(row.source, row.token))}
                                >
                                    <LinkIcon />
                                </Button>
                            </Tooltip>
                            <Button
                                disabled={!auth.hasPerm('problem.edit')}
                                sx={{ color: theme.palette.text.primary }}
                                onClick={() => deleteItem(row.id, row.cod_empresa)}
                            >
                                <DeleteIcon />
                            </Button>
                        </>
                    )
                }
            }
        }
    ]

    const options = {
        filterType: 'checkbox',
        rowsPerPageOptions: [30, 50, 100],
        rowsPerPage: 30,
        rowHover: false,
        print: false,
        selectableRows: 'none',
        download: false,
        customToolbar: () => (
            <>
                <Tooltip title="Guia de configuracao">
                    <IconButton
                        onClick={() => setHandlerGuia(true)}
                        sx={{ color: theme.palette.text.primary }}
                    >
                        <HelpOutlineIcon />
                    </IconButton>
                </Tooltip>
                <IconButton
                    disabled={!auth.hasPerm('problem.edit')}
                    onClick={() => setHandlerDialog(true)}
                    sx={{ color: theme.palette.text.primary }}
                >
                    <AddIcon />
                </IconButton>
            </>
        ),
    }

    return (
        <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
            <ThemeProvider theme={theme}>
                <Snackbar
                    open={snack.open}
                    autoHideDuration={2000}
                    onClose={() => setSnack({ open: false, message: '' })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity="success" onClose={() => setSnack({ open: false, message: '' })}>
                        {snack.message}
                    </Alert>
                </Snackbar>

                <FormDialog
                    open={handlerDialog}
                    handlerClose={() => setHandlerDialog(false)}
                    updateData={updateData}
                />
                <GuiaWebhooks
                    open={handlerGuia}
                    handlerClose={() => setHandlerGuia(false)}
                />

                {/* Banner de ajuda */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1,
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                            Webhooks permitem que o Zabbix e Wazuh enviem alarmes para o Horus em tempo real.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Crie um token, copie a URL e configure no sistema externo. Clique em <b>?</b> na tabela para o guia completo.
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setHandlerGuia(true)}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Guia de configuracao
                    </Button>
                </Paper>

                <Box sm={12} lg={12}>
                    <MUIDataTable
                        title={"Webhook Tokens"}
                        data={data}
                        columns={columns}
                        options={options}
                    />
                </Box>
            </ThemeProvider>
        </Container>
    )
}
